import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Phone, Envelope, Buildings, MapPin, Receipt, Bell, ShoppingCart, CurrencyInr,
  PencilSimple, ClipboardText, Kanban,
} from "@phosphor-icons/react";

function StatChip({ label, value, tone = "default" }) {
  const cls = tone === "danger"
    ? "border-destructive/40 bg-destructive/10 text-destructive"
    : tone === "warn"
    ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
    : "border-border bg-secondary/40";
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-mono text-sm font-semibold">{value}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon size={12} weight="duotone" className="mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <span className="text-muted-foreground mr-1.5">{label}:</span>
        <span className="text-foreground break-words">{value}</span>
      </div>
    </div>
  );
}

const inr = (v) => "₹" + (v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function ActivityRow({ testid, icon: Icon, title, sub, right }) {
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-0" data-testid={testid}>
      <Icon size={12} weight="duotone" className="text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs truncate">{title}</div>
        {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
      </div>
      {right && <div className="text-[11px] font-mono text-muted-foreground shrink-0">{right}</div>}
    </div>
  );
}

export default function CustomerProfilePanel({ customerId, canEditCustomer, onEdit, onView }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) { setData(null); return; }
    setLoading(true);
    api.get(`/customers/${customerId}/profile`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (!customerId) {
    return (
      <Card className="p-4 h-full" data-testid="customer-profile-empty">
        <div className="text-xs text-muted-foreground italic">Select a customer to see their profile, outstanding balance and recent history.</div>
      </Card>
    );
  }
  if (loading || !data) {
    return <Card className="p-4 h-full" data-testid="customer-profile-loading"><div className="text-xs text-muted-foreground">Loading profile…</div></Card>;
  }

  const c = data.customer;
  const o = data.outstanding;
  const counts = data.counts;
  const r = data.recent;

  return (
    <Card className="p-4 h-full space-y-4 overflow-y-auto max-h-[80vh]" data-testid="customer-profile-panel">
      {/* Identity */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="overline text-muted-foreground text-[10px]">Customer</div>
            <div className="font-heading font-bold text-lg leading-tight truncate" data-testid="cp-name">{c.customer_name}</div>
            {c.company_name && <div className="text-xs text-muted-foreground truncate" data-testid="cp-company">{c.company_name}</div>}
          </div>
          <div className="flex gap-1 shrink-0">
            {onView && (
              <button onClick={onView} className="p-1.5 rounded border border-border hover:bg-secondary" title="View customer" data-testid="cp-view">
                <ClipboardText size={12} weight="duotone" />
              </button>
            )}
            {canEditCustomer && onEdit && (
              <button onClick={onEdit} className="p-1.5 rounded border border-border hover:bg-secondary" title="Edit customer" data-testid="cp-edit">
                <PencilSimple size={12} weight="duotone" />
              </button>
            )}
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          <InfoRow icon={Phone}     label="Mobile"   value={c.mobile} />
          <InfoRow icon={Envelope}  label="Email"    value={c.email} />
          <InfoRow icon={Buildings} label="GSTIN"    value={c.gst} />
          <InfoRow icon={MapPin}    label="Billing"  value={c.address} />
          {c.city && <InfoRow icon={MapPin} label="City" value={[c.city, c.state, c.pincode].filter(Boolean).join(", ")} />}
        </div>
      </div>

      {/* Outstanding — dual view */}
      <div>
        <div className="overline text-muted-foreground text-[10px] mb-2">Outstanding Balance</div>
        <div className="grid grid-cols-2 gap-2" data-testid="cp-outstanding">
          <StatChip label="Invoice-based" value={inr(o.invoiced)}      tone={o.invoiced > 0 ? "danger" : "default"} />
          <StatChip label="Payment gap"   value={inr(o.payment_gap)}  tone={o.payment_gap > 0 ? "warn" : "default"} />
        </div>
        <div className="text-[10px] text-muted-foreground mt-1.5">
          Invoiced {inr(o.total_invoiced)} · Paid {inr(o.total_paid)}{o.total_refunded > 0 ? ` · Refunded ${inr(o.total_refunded)}` : ""}
        </div>
      </div>

      {/* Activity summary */}
      <div>
        <div className="overline text-muted-foreground text-[10px] mb-2">Activity</div>
        <div className="grid grid-cols-3 gap-1 text-center text-[10px]">
          <div className="p-1.5 rounded bg-secondary/40" data-testid="cp-count-leads"><div className="font-mono font-semibold">{counts.leads}</div><div className="text-muted-foreground">Leads</div></div>
          <div className="p-1.5 rounded bg-secondary/40" data-testid="cp-count-orders"><div className="font-mono font-semibold">{counts.orders}</div><div className="text-muted-foreground">Orders</div></div>
          <div className="p-1.5 rounded bg-secondary/40" data-testid="cp-count-quotations"><div className="font-mono font-semibold">{counts.quotations}</div><div className="text-muted-foreground">Quotes</div></div>
          <div className="p-1.5 rounded bg-secondary/40" data-testid="cp-count-invoices"><div className="font-mono font-semibold">{counts.invoices}</div><div className="text-muted-foreground">Invoices</div></div>
          <div className="p-1.5 rounded bg-secondary/40" data-testid="cp-count-payments"><div className="font-mono font-semibold">{counts.payments}</div><div className="text-muted-foreground">Payments</div></div>
          <div className="p-1.5 rounded bg-secondary/40" data-testid="cp-count-followups"><div className="font-mono font-semibold">{counts.followups}</div><div className="text-muted-foreground">Follow-ups</div></div>
        </div>
      </div>

      {/* Timeline of previous transactions */}
      {r.orders.length > 0 && (
        <div data-testid="cp-recent-orders">
          <div className="overline text-muted-foreground text-[10px] mb-1">Recent Orders</div>
          {r.orders.slice(0, 3).map(o2 => (
            <ActivityRow key={o2.id} testid={`cp-order-${o2.id}`}
              icon={ShoppingCart} title={`${o2.number} · ${o2.order_status}`}
              sub={o2.payment_status} right={inr(o2.total)} />
          ))}
        </div>
      )}
      {r.quotations.length > 0 && (
        <div data-testid="cp-recent-quotations">
          <div className="overline text-muted-foreground text-[10px] mb-1">Recent Quotations</div>
          {r.quotations.slice(0, 3).map(q => (
            <ActivityRow key={q.id} testid={`cp-quotation-${q.id}`}
              icon={Receipt} title={`${q.number} · ${q.status}`} right={inr(q.total)} />
          ))}
        </div>
      )}
      {r.invoices.length > 0 && (
        <div data-testid="cp-recent-invoices">
          <div className="overline text-muted-foreground text-[10px] mb-1">Recent Invoices</div>
          {r.invoices.slice(0, 3).map(i => (
            <ActivityRow key={i.id} testid={`cp-invoice-${i.id}`}
              icon={Receipt} title={`${i.number} · ${i.status}`}
              sub={i.balance > 0 ? `Balance ${inr(i.balance)}` : "Cleared"} right={inr(i.total)} />
          ))}
        </div>
      )}
      {r.payments.length > 0 && (
        <div data-testid="cp-recent-payments">
          <div className="overline text-muted-foreground text-[10px] mb-1">Recent Payments</div>
          {r.payments.slice(0, 3).map(p => (
            <ActivityRow key={p.id} testid={`cp-payment-${p.id}`}
              icon={CurrencyInr} title={`${p.type} · ${p.method}`} right={inr(p.amount)} />
          ))}
        </div>
      )}
      {r.followups.length > 0 && (
        <div data-testid="cp-recent-followups">
          <div className="overline text-muted-foreground text-[10px] mb-1">Recent Follow-ups</div>
          {r.followups.slice(0, 3).map(f => (
            <ActivityRow key={f.id} testid={`cp-followup-${f.id}`}
              icon={Bell} title={`${f.channel} · ${f.status}`}
              sub={f.notes ? f.notes.slice(0, 40) : ""} right={f.next_date} />
          ))}
        </div>
      )}
      {r.leads.length > 0 && (
        <div data-testid="cp-recent-leads">
          <div className="overline text-muted-foreground text-[10px] mb-1">Recent Leads</div>
          {r.leads.slice(0, 3).map(l => (
            <ActivityRow key={l.id} testid={`cp-lead-${l.id}`}
              icon={Kanban} title={`${l.product_interest || "Lead"} · ${l.stage}`} right={inr(l.value)} />
          ))}
        </div>
      )}
      {counts.orders === 0 && counts.quotations === 0 && counts.invoices === 0 && counts.payments === 0 && counts.followups === 0 && counts.leads === 0 && (
        <div className="text-xs text-muted-foreground italic pt-2">No previous transactions yet — this will be the customer's first record.</div>
      )}
    </Card>
  );
}
